import { useTranslation } from "react-i18next";
import { Wallet } from "lucide-react";
import { PATHS } from "@/routes/paths";
import { useMsGetLeaveBalanceStatsQuery } from "@/services/(my-space)/api.leaves";
import { Spinner } from "@/components/ui/v2/spinner";
import { DashboardCard } from "@/components/common/DashboardCard";
import { ICON_MAP } from "@/constants/leave-management";

const LEAVE_TYPE_COLORS = [
  { bg: "var(--color-warning-muted)", fg: "var(--color-warning)" },
  { bg: "var(--color-danger-muted)", fg: "var(--color-danger)" },
  { bg: "var(--color-info-muted)", fg: "var(--color-primary)" },
  { bg: "var(--color-success-muted)", fg: "var(--color-success)" },
  { bg: "var(--color-info-muted)", fg: "var(--color-info)" },
];

export const LeaveBalancesWidget = ({ className }: { className?: string }) => {
  const { t } = useTranslation("my-space");

  const { data: { data: leaveBalances } = {}, isFetching } =
    useMsGetLeaveBalanceStatsQuery();

  const leaveBalanceData =
    leaveBalances
      ?.slice()
      ?.sort(
        (a, b) => Number(b.isSystemGenerated) - Number(a.isSystemGenerated),
      )
      ?.slice(0, 4)
      ?.map((leave, index) => ({
        title: leave.leaveTypeName,
        allocated: leave.allocated,
        used: leave.used,
        remaining: leave.remaining,
        Icon: ICON_MAP[leave.leaveTypeIcon] ?? Wallet,
        ...LEAVE_TYPE_COLORS[index % LEAVE_TYPE_COLORS.length],
      })) ?? [];

  return (
    <DashboardCard
      icon="/icons/interface-icons/sidebar/icon-3d-ms-leaves.png"
      title={t("dashboard.leave_balance")}
      rootClassname={className}
      action={{
        label: t("ui.view_history"),
        link: PATHS.MY_SPACE.MS_LEAVES_MANAGEMENT_PATHS.ROOT,
      }}
    >
      {isFetching ? (
        <div className="h-full flex items-center justify-center">
          <Spinner />
        </div>
      ) : !leaveBalanceData?.length ? (
        <div className="h-full flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Wallet className="size-6" />
          <p className="text-xs font-medium">
            {t("dashboard.no_leave_balance")}
          </p>
        </div>
      ) : (
        <div className="h-full grid grid-cols-2 min-[950px]:flex gap-3">
          {leaveBalanceData.map((item) => (
            <div
              key={item.title}
              className="relative flex flex-col items-center justify-center text-center gap-4 p-3 ps-4 rounded-sm overflow-hidden min-w-0 min-[950px]:flex-1"
              style={{ backgroundColor: item.bg }}
            >
              <span
                className="absolute start-0 top-0 bottom-0 w-[3px]"
                style={{ backgroundColor: item.fg }}
              />

              <div className="flex items-center justify-center rounded-full size-11 shrink-0 bg-white">
                <item.Icon className="size-5.5" style={{ color: item.fg }} />
              </div>

              <div className="min-w-0">
                <p className="text-base font-bold leading-none tabular-nums whitespace-nowrap text-foreground">
                  {item.remaining}
                  <span className="text-[11px] font-medium text-muted-foreground">
                    {" "}
                    / {item.allocated}
                  </span>
                </p>
                <p className="mt-2 text-[11px] font-semibold capitalize leading-tight text-muted-foreground">
                  {item.title}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardCard>
  );
};
